import React from 'react';
import { remToPx, Box, ShortText, Title } from 'src/viewLayer/components/DesignSystem';

import TextWithInterpolation from 'src/viewLayer/components/shared/TextWithInterpolation';
import TextWithPartialCustomStyling from 'src/viewLayer/components/shared/TextWithPartialCustomStyling';
import { t } from 'src/viewLayer/helpers/translations';
import { getImageComponent } from 'src/viewLayer/assets/images';
import ButtonSection from './ButtonSection';
import { LOCALE, GEO_DATA } from 'src/dataLayer/env';
import type { BoldTextType, EmailConfirmationPropsType } from '../types';
import { ContentStack, EmailConfirmationWrapper, MainImageWrapper, MaintenanceText } from './index.styled';

const BoldText: React.FC<BoldTextType> = ({ children, ...props }): JSX.Element =>
  <span style={ { fontWeight: 600 } } { ...props }>{ children }</span>;
const countriesROA = new Set(['ARG', 'COL', 'PER', 'PRY', 'URY']);

export default ({
  nodeConfig: { name, variables = {}, options = {} },
  goToNextNode,
  email: userEmail
}: EmailConfirmationPropsType): JSX.Element => {
  const {
    title,
    image,
    content,
    content_confirm,
    content_spam,
    button
  } = variables;
  const { hideEmailConfirmationNode = false, showContinueButton = false } = options;
  const delayedConfirmationEmails = false;
  const delayedConfirmationEmailsText = LOCALE === 'uk' ? undefined : t('pages.evp.verification_account_created.content_email_maintenance');
  const { countryCode3 } = GEO_DATA;
  const showRedirectToDashboard = hideEmailConfirmationNode && countriesROA.has(countryCode3);
  if (showRedirectToDashboard) {
    goToNextNode();
  }
  return (
    <EmailConfirmationWrapper id={`uat-${name}`} direction="stack" alignItems="center" padding={remToPx(1)} gap={32}>
      <MainImageWrapper
        padding={{ xxsmall: [remToPx(2), 0, 0], xsmall: [remToPx(5), 0, 0] }}
      >{getImageComponent(image) }</MainImageWrapper>
      <Box>
        <Title size={{ xxxsmall: 'small', medium: 'default' }} textAlign="center">{t(title.key)}</Title>
      </Box>
      <ContentStack gap="1.2rem">
        <ShortText>
          {
            TextWithPartialCustomStyling({
              translationText: t(content.key_interpolated, { userEmail }) || t(content.key),
              customStyledComponents: [BoldText]
            }).map((TextPiece: JSX.Element) => TextPiece)
          }
        </ShortText>
        <ShortText>
          { content_confirm
            ? TextWithInterpolation({ translationText: t(content_confirm.key_interpolated) })
            : <span style={ { fontWeight: 600 } }>{ userEmail }</span>
          }
        </ShortText>
      </ContentStack>
      <ButtonSection
        userEmail={userEmail}
        continueHandler={goToNextNode}
        button={button}
        showContinueButton={showContinueButton}
      />
      { content_spam &&
        <ShortText size="small">{ t('pages.evp.verification_account_created.content_spam') }</ShortText>
      }
      { delayedConfirmationEmails && <MaintenanceText size="small">{ delayedConfirmationEmailsText }</MaintenanceText>
      }
    </EmailConfirmationWrapper>
  );
};
